export type HttpErrorCode =
  | 'TIMEOUT'
  | 'NETWORK'
  | 'HTTP_4XX'
  | 'HTTP_5XX'
  | 'RATE_LIMITED'
  | 'ABORTED'

export class HttpError extends Error {
  readonly code: HttpErrorCode
  readonly status: number | undefined
  override readonly cause: unknown

  constructor(code: HttpErrorCode, message: string, status?: number, cause?: unknown) {
    super(message)
    this.name = 'HttpError'
    this.code = code
    this.status = status
    this.cause = cause
  }
}

export interface FetchRetryOptions {
  /** Per-attempt timeout in ms. Defaults to 5000. */
  timeoutMs?: number
  /** Number of retries after the first attempt. Defaults to 2 (3 attempts total). */
  retries?: number
  /** Base for exponential backoff in ms. Defaults to 200 (200, 400, 800). */
  backoffBaseMs?: number
  /** External AbortSignal to cooperate with caller cancellation. */
  signal?: AbortSignal
}

/**
 * `fetch` wrapper with per-attempt timeout, retries with exponential backoff
 * and structured errors. Retries on 5xx, 429, network failures and timeouts.
 * Does not retry on 4xx (except 429) — those are client-side bugs surfaced
 * immediately.
 *
 * Calling code typically catches `HttpError` and wraps it into a domain
 * error in the source-specific client (BCB or BoC).
 */
export async function fetchWithRetry(
  url: string,
  options: FetchRetryOptions = {},
): Promise<Response> {
  const timeoutMs = options.timeoutMs ?? 5000
  const retries = options.retries ?? 2
  const backoffBaseMs = options.backoffBaseMs ?? 200
  const externalSignal = options.signal

  let lastError: HttpError = new HttpError('NETWORK', 'unknown error')

  for (let attempt = 0; attempt <= retries; attempt++) {
    if (externalSignal?.aborted) {
      throw new HttpError('ABORTED', 'aborted by caller')
    }

    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(new Error('timeout')), timeoutMs)

    const onExternalAbort = (): void => {
      controller.abort(externalSignal?.reason)
    }
    externalSignal?.addEventListener('abort', onExternalAbort)

    try {
      const response = await fetch(url, { signal: controller.signal })

      if (response.ok) return response

      if (response.status >= 400 && response.status < 500 && response.status !== 429) {
        throw new HttpError('HTTP_4XX', `HTTP ${response.status}`, response.status)
      }

      lastError = new HttpError(
        response.status === 429 ? 'RATE_LIMITED' : 'HTTP_5XX',
        `HTTP ${response.status}`,
        response.status,
      )
    } catch (err) {
      if (err instanceof HttpError) throw err

      if (externalSignal?.aborted) {
        throw new HttpError('ABORTED', 'aborted by caller', undefined, err)
      }

      const isTimeout = controller.signal.aborted
      lastError = isTimeout
        ? new HttpError('TIMEOUT', `timeout after ${timeoutMs}ms`, undefined, err)
        : new HttpError(
            'NETWORK',
            err instanceof Error ? err.message : String(err),
            undefined,
            err,
          )
    } finally {
      clearTimeout(timer)
      externalSignal?.removeEventListener('abort', onExternalAbort)
    }

    if (attempt < retries) {
      const delay = backoffBaseMs * 2 ** attempt
      await sleep(delay)
    }
  }

  throw lastError
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
